import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BusinessOutlined as BusinessOutlinedIcon,
  OpenInNew as OpenInNewIcon,
  PersonOutline as PersonOutlineIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  Report as ReportIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Bar } from 'react-chartjs-2';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { NewsCluster, TimelineEvent, WikiConcept } from '../types/types';
import { getClusterKeywordParts } from './radar/RadarTableCells';

ChartJS.register(BarElement, CategoryScale, LinearScale, Title, ChartTooltip, Legend);

export type ClusterRadarDetailPanelProps = {
  cluster: NewsCluster;
  /** When set (e.g. Custom & relevant tab), article rows include an Origin column. */
  feedOrigin?: 'custom' | 'relevant';
  onArticleFeedback?: (clusterId: string, articleUrl: string) => Promise<void>;
  incorrectArticles?: string[];
  /** Library cluster drawer: compact insights row, entities, horizontal timeline, no horizontal table scroll. */
  layoutVariant?: 'radar' | 'library';
};

/**
 * `general_overview` / `detailed_summary` describe the cluster as a whole. Showing them on every
 * article row repeats the same text for multi-article clusters. Only attach that copy when there is
 * a single article (standalone radar rows and true singleton clusters).
 */
function overviewLineForArticleRow(cluster: NewsCluster): string {
  const n = cluster.articles?.length ?? 0;
  if (n !== 1) return '—';
  const g = cluster.general_overview?.trim();
  if (g) return g;
  const d = cluster.detailed_summary?.trim();
  if (d) return d;
  return '—';
}

const originChip = (origin: 'custom' | 'relevant') => (
  <Chip
    label={origin === 'custom' ? 'Custom' : 'Relevant'}
    size="small"
    sx={{
      height: 22,
      fontWeight: 700,
      fontSize: '0.7rem',
      borderRadius: 0,
      bgcolor: origin === 'custom' ? '#1e3a8a' : '#581c87',
      color: origin === 'custom' ? '#bfdbfe' : '#e9d5ff',
      border: `1px solid ${origin === 'custom' ? '#60a5fa' : '#c084fc'}`,
    }}
  />
);

function parseTimelineDate(d: string): number {
  const t = Date.parse(d);
  return Number.isFinite(t) ? t : 0;
}

function ClusterHorizontalTimeline({ events }: { events: TimelineEvent[] }) {
  const sorted = useMemo(() => {
    return [...events].sort((a, b) => parseTimelineDate(a.event_date) - parseTimelineDate(b.event_date));
  }, [events]);

  if (!sorted.length) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fafafa', mb: 1 }}>
        Timeline
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 0,
          overflowX: 'auto',
          pb: 1,
          borderBottom: '1px solid #27272a',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#3f3f46', borderRadius: 3 },
        }}
      >
        {sorted.map((ev, idx) => (
          <Box
            key={`${idx}-${ev.event_date}`}
            sx={{
              flex: '0 0 auto',
              minWidth: 140,
              maxWidth: 200,
              pr: 2,
              borderRight: idx < sorted.length - 1 ? '1px solid #27272a' : 'none',
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#2dd4bf',
                  boxShadow: '0 0 0 3px rgba(45,212,191,0.2)',
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" sx={{ color: '#a5f3fc', fontWeight: 700, fontSize: '0.68rem' }}>
                {ev.event_date}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: '#d4d4d8',
                fontSize: '0.75rem',
                lineHeight: 1.35,
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {ev.event_description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const entityChipSx = {
  height: 24,
  borderRadius: 0,
  bgcolor: '#18181b',
  color: '#e4e4e7',
  border: '1px solid #3f3f46',
  '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
} as const;

/** Fixed plot height so sentiment + title aligns with Mentioned entities block. */
const LIBRARY_SENTIMENT_PLOT_HEIGHT_PX = 112;

function LibraryEntityRows({ concepts }: { concepts: WikiConcept[] }) {
  const grouped = useMemo(() => {
    const persons = concepts.filter((c) => c.wiki_type === 'persons');
    const orgs = concepts.filter((c) => c.wiki_type === 'organizations');
    const places = concepts.filter((c) => c.wiki_type === 'locations');
    return { persons, orgs, places };
  }, [concepts]);

  const iconTile = (node: React.ReactNode, tooltip: string) => (
    <Tooltip title={tooltip} arrow>
      <Box
        aria-label={tooltip}
        sx={{
          width: 30,
          height: 30,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#18181b',
          border: '1px solid #3f3f46',
          borderRadius: 0,
          boxSizing: 'border-box',
        }}
      >
        {node}
      </Box>
    </Tooltip>
  );

  const row = (tooltip: string, icon: React.ReactNode, items: WikiConcept[], keyPrefix: string) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
      {iconTile(icon, tooltip)}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, minWidth: 0, alignItems: 'center', minHeight: 30 }}>
        {items.length === 0 ? (
          <Typography variant="caption" sx={{ color: '#52525b', lineHeight: '30px' }}>
            —
          </Typography>
        ) : (
          items.map((c, i) => (
            <Chip
              key={`${keyPrefix}-${i}-${c.concept}`}
              label={c.concept}
              size="small"
              onClick={
                c.wiki_url ? () => window.open(c.wiki_url, '_blank', 'noopener,noreferrer') : undefined
              }
              sx={{
                ...entityChipSx,
                cursor: c.wiki_url ? 'pointer' : 'default',
              }}
            />
          ))
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        flex: '1 1 220px',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fafafa', mb: 1, flexShrink: 0 }}>
        Mentioned entities
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          minHeight: LIBRARY_SENTIMENT_PLOT_HEIGHT_PX,
          justifyContent: 'center',
        }}
      >
        {row(
          'People',
          <PersonOutlineIcon sx={{ fontSize: 17, color: '#94a3b8' }} />,
          grouped.persons,
          'p'
        )}
        {row(
          'Organizations',
          <BusinessOutlinedIcon sx={{ fontSize: 17, color: '#94a3b8' }} />,
          grouped.orgs,
          'o'
        )}
        {row('Places', <PlaceOutlinedIcon sx={{ fontSize: 17, color: '#94a3b8' }} />, grouped.places, 'l')}
      </Box>
    </Box>
  );
}

const ClusterRadarDetailPanel: React.FC<ClusterRadarDetailPanelProps> = ({
  cluster,
  feedOrigin,
  onArticleFeedback,
  incorrectArticles = [],
  layoutVariant = 'radar',
}) => {
  const isLibrary = layoutVariant === 'library';

  const { chartData, chartOptions } = useMemo(() => {
    const sentimentScores = cluster.articles?.map((a) => a.sentiment_score || 0) || [];
    const binWidth = 0.1;
    const numBins = Math.ceil(1 / binWidth);
    const bins = new Array(numBins).fill(0);
    sentimentScores.forEach((score) => {
      let binIndex = Math.floor(score / binWidth);
      if (binIndex >= numBins) binIndex = numBins - 1;
      bins[binIndex]++;
    });
    const binLabels: string[] = [];
    for (let i = 0; i < numBins; i++) {
      binLabels.push(`<${((i + 1) * binWidth).toFixed(1)}`);
    }
    const data = {
      labels: binLabels,
      datasets: [
        {
          label: 'Number of articles',
          data: bins,
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    };
    const tickSize = isLibrary ? 8 : 10;
    const titleSize = isLibrary ? 10 : 12;
    const options = {
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: { display: false },
          title: {
            display: true,
            text: 'Sentiment score',
            color: '#a1a1aa',
            font: { size: titleSize },
          },
          ticks: { color: '#a1a1aa', maxRotation: 45, minRotation: 0, font: { size: tickSize } },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: 'Articles', color: '#a1a1aa', font: { size: titleSize } },
          ticks: { stepSize: 1, color: '#a1a1aa', font: { size: tickSize } },
        },
      },
      plugins: {
        legend: { display: false },
      },
    };
    return { chartData: data, chartOptions: options };
  }, [cluster.articles, isLibrary]);

  const formatDate = (dateString: string) => format(new Date(dateString), 'dd/MM/yyyy');
  const keywordList = useMemo(() => getClusterKeywordParts(cluster), [cluster]);

  const sentimentBarWidthPx = isLibrary ? 88 : 120;

  const renderSentimentBar = (score: number | undefined) => {
    if (score === undefined) return null;
    const scorePercentage = score * 100;
    return (
      <Box sx={{ position: 'relative', width: `${sentimentBarWidthPx}px`, height: '18px' }}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '9px',
            background: 'linear-gradient(to right, #FFB3BA, #FFFFBA, #BAFFC9)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '-22px',
            left: `${scorePercentage}%`,
            transform: 'translateX(-50%)',
            padding: '1px 4px',
            background: '#27272a',
            border: '1px solid #52525b',
            borderRadius: '3px',
            fontSize: '11px',
            whiteSpace: 'nowrap',
            color: '#fafafa',
          }}
        >
          {(score * 100).toFixed(1)}%
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: `${scorePercentage}%`,
            transform: 'translateX(-50%)',
            width: '2px',
            height: '100%',
            backgroundColor: '#94a3b8',
          }}
        />
      </Box>
    );
  };

  const wikiConcepts = cluster.wiki_concepts || [];
  const timelineEvents = cluster.timeline || [];

  const keywordsBlock = keywordList.length > 0 && (
    <Box sx={{ minWidth: 0, width: '100%' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fafafa', mb: 1 }}>
        Keywords
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
        }}
      >
        {keywordList.map((kw, idx) => (
          <Chip
            key={`${idx}-${kw}`}
            label={kw}
            size="small"
            sx={{
              height: 26,
              borderRadius: 0,
              bgcolor: '#27272a',
              color: '#fafafa',
              border: '1px solid #52525b',
              flexShrink: 0,
              '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
            }}
          />
        ))}
      </Box>
    </Box>
  );

  const sentimentChartBlock = (
    <Box
      sx={{
        flex: isLibrary ? '0 0 auto' : '1 1 auto',
        width: '100%',
        minHeight: 0,
        ...(isLibrary
          ? {
              display: 'flex',
              flexDirection: 'column',
            }
          : {}),
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 'bold',
          color: '#fafafa',
          mb: 1,
          textAlign: isLibrary ? 'left' : 'center',
          fontSize: isLibrary ? '0.9rem' : undefined,
          flexShrink: 0,
        }}
      >
        Sentiment score distribution
      </Typography>
      <Box
        sx={{
          ...(isLibrary
            ? {
                flex: 'none',
                height: LIBRARY_SENTIMENT_PLOT_HEIGHT_PX,
                width: '100%',
                maxWidth: 208,
                position: 'relative',
              }
            : {
                height: 220,
                width: '100%',
                maxWidth: 520,
                mx: 'auto',
              }),
        }}
      >
        <Bar data={chartData} options={chartOptions as any} />
      </Box>
    </Box>
  );

  const detailedSummaryBlock = (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fafafa', mb: 1 }}>
        Detailed summary
      </Typography>
      <Typography variant="body2" sx={{ color: '#d4d4d8', whiteSpace: 'pre-wrap' }}>
        {cluster.detailed_summary || '—'}
      </Typography>
    </Box>
  );

  const libraryKeywordsSection =
    keywordList.length > 0 ? (
      keywordsBlock
    ) : (
      <Box sx={{ minWidth: 0, width: '100%' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fafafa', mb: 1 }}>
          Keywords
        </Typography>
        <Typography variant="body2" sx={{ color: '#52525b' }}>
          —
        </Typography>
      </Box>
    );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, pb: 4 }}>
      {isLibrary ? (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
              alignItems: 'stretch',
            }}
          >
            <Box sx={{ width: '100%', minWidth: 0 }}>{libraryKeywordsSection}</Box>
            <Box sx={{ width: '100%', minWidth: 0 }}>{detailedSummaryBlock}</Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              gap: 2,
              rowGap: 2,
            }}
          >
            <Box
              sx={{
                flex: '0 1 220px',
                width: 'min(100%, 220px)',
                maxWidth: 220,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {sentimentChartBlock}
            </Box>
            <LibraryEntityRows concepts={wikiConcepts} />
          </Box>
        </>
      ) : (
        <>
          {detailedSummaryBlock}
          {keywordsBlock}
          <Box>{sentimentChartBlock}</Box>
        </>
      )}

      {isLibrary && timelineEvents.length > 0 ? <ClusterHorizontalTimeline events={timelineEvents} /> : null}

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fafafa', mb: 1 }}>
          Articles
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            bgcolor: '#09090b',
            border: '1px solid #27272a',
            overflowX: 'hidden',
            maxWidth: '100%',
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{ tableLayout: 'fixed', width: '100%' }}
          >
            <TableHead>
              <TableRow>
                {feedOrigin && (
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 'bold', color: '#fafafa', bgcolor: '#27272a', borderColor: '#27272a' }}
                  >
                    Origin
                  </TableCell>
                )}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    color: '#fafafa',
                    bgcolor: '#27272a',
                    borderColor: '#27272a',
                    width: isLibrary ? '12%' : '14%',
                  }}
                >
                  Source
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    color: '#fafafa',
                    bgcolor: '#27272a',
                    borderColor: '#27272a',
                    width: isLibrary ? '35%' : 'auto',
                  }}
                >
                  {'Title & overview'}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    color: '#fafafa',
                    bgcolor: '#27272a',
                    borderColor: '#27272a',
                    width: isLibrary ? '16%' : '14%',
                  }}
                >
                  Sentiment
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    color: '#fafafa',
                    bgcolor: '#27272a',
                    borderColor: '#27272a',
                    width: isLibrary ? '14%' : '12%',
                  }}
                >
                  Published
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    color: '#fafafa',
                    bgcolor: '#27272a',
                    borderColor: '#27272a',
                    width: isLibrary ? '12%' : '11%',
                    minWidth: 120,
                    whiteSpace: 'nowrap',
                    px: 0.75,
                  }}
                >
                  Incorrect?
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    color: '#fafafa',
                    bgcolor: '#27272a',
                    borderColor: '#27272a',
                    width: isLibrary ? '11%' : '8%',
                    minWidth: 52,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Link
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(cluster.articles || []).map((article, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    bgcolor: idx % 2 === 0 ? '#09090b' : '#18181b',
                    '&:hover': { bgcolor: '#27272a' },
                  }}
                >
                  {feedOrigin && (
                    <TableCell align="center" sx={{ borderColor: '#27272a', verticalAlign: 'middle' }}>
                      {originChip(feedOrigin)}
                    </TableCell>
                  )}
                  <TableCell
                    align="center"
                    sx={{
                      color: '#fafafa',
                      borderColor: '#27272a',
                      verticalAlign: 'middle',
                      wordBreak: 'break-word',
                      overflow: 'hidden',
                    }}
                  >
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }} title={article.source}>
                      {article.source}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#d4d4d8',
                      borderColor: '#27272a',
                      verticalAlign: 'top',
                      wordBreak: 'break-word',
                      overflow: 'hidden',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, mb: 0.25, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      title={article.article_title}
                    >
                      {article.article_title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#a1a1aa',
                        display: '-webkit-box',
                        WebkitLineClamp: isLibrary ? 4 : 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.35,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {overviewLineForArticleRow(cluster)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ borderColor: '#27272a', verticalAlign: 'middle' }}>
                    <Box display="flex" justifyContent="center">{renderSentimentBar(article.sentiment_score)}</Box>
                  </TableCell>
                  <TableCell align="center" sx={{ color: '#fafafa', borderColor: '#27272a', verticalAlign: 'middle' }}>
                    {formatDate(article.published_at)}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      borderColor: '#27272a',
                      minWidth: 120,
                      width: isLibrary ? '12%' : '11%',
                      whiteSpace: 'nowrap',
                      verticalAlign: 'middle',
                    }}
                  >
                    <Tooltip title="Mark article as incorrect for this cluster">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => onArticleFeedback?.(cluster.title, article.url)}
                          disabled={!onArticleFeedback}
                          sx={{ color: incorrectArticles.includes(article.url) ? '#f87171' : '#a1a1aa' }}
                        >
                          <ReportIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      borderColor: '#27272a',
                      minWidth: 52,
                      width: isLibrary ? '11%' : '8%',
                      whiteSpace: 'nowrap',
                      verticalAlign: 'middle',
                    }}
                  >
                    {article.url ? (
                      <Tooltip title="Open article in new tab">
                        <IconButton
                          component="a"
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          aria-label="Open article in new tab"
                          sx={{ color: '#a1a1aa', '&:hover': { color: '#60a5fa' } }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" sx={{ color: '#52525b' }}>
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default ClusterRadarDetailPanel;
